import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBulkCreateSignup } from './useBulkCreateSignup';
import { useBulkDeleteSignup } from './useBulkDeleteSignup';
import { BulkCreateSignupResponse } from '@amzn/red-velvet-api';
import { AmazonSignup, SignupStatus } from '../../utils/signupUtils';

function isUserFailedToBeSignedUp(
  response: BulkCreateSignupResponse,
  shiftId: string,
  alias: string
) {
  const shiftResponse = response.shiftSignupResponseMap[shiftId];
  if (!shiftResponse) return false;
  const failedUsers = [
    ...shiftResponse.failedToSignupUsers,
    ...shiftResponse.noGiveHubAccessSignupUsers,
  ];
  return failedUsers.some((u) => u.identifierValue === alias);
}

export function useInlineSignupStatusEdit(eventId: string) {
  // Local state for status changes, keyed by signupId
  const [localInlineSignupStatusChanges, setLocalInlineSignupStatusChanges] = useState<
    Record<string, SignupStatus>
  >({});

  // Maps old signupId -> new signupId after re-signup
  const [localSignupIdUpdates, setLocalSignupIdUpdates] = useState<Record<string, string>>({});

  // Cancel modal state
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [pendingCancelItem, setPendingCancelItem] = useState<AmazonSignup | null>(null);

  const queryClient = useQueryClient();
  const { mutate: bulkCreateSignup, isPending: isCreatePending } = useBulkCreateSignup(eventId);
  const { mutate: bulkDeleteSignup, isPending: isDeletePending } = useBulkDeleteSignup(eventId);

  const revertLocalChangeForSignupStatus = (signupId: string) => {
    setLocalInlineSignupStatusChanges((prev) => {
      const updated = { ...prev };
      delete updated[signupId];
      return updated;
    });
  };

  const applyLocalChangesForSignupStatus = (signups: AmazonSignup[]) =>
    signups.map((item) => {
      const newSignupId = localSignupIdUpdates[item.signupId] ?? item.signupId;
      return {
        ...item,
        signupId: newSignupId,
        signupStatus: localInlineSignupStatusChanges[newSignupId] ?? item.signupStatus,
      };
    });

  const handleCancelConfirm = () => {
    if (pendingCancelItem) {
      setLocalInlineSignupStatusChanges((prev) => ({
        ...prev,
        [pendingCancelItem.signupId]: SignupStatus.PENDING,
      }));
      bulkDeleteSignup(
        { signupRequests: [pendingCancelItem.signupId] },
        {
          onSuccess: (response) => {
            response.failedSignupIds?.forEach((failedId) =>
              revertLocalChangeForSignupStatus(failedId)
            );
            response.succeededSignupIds?.forEach((succeededSignupId) =>
              setLocalInlineSignupStatusChanges((prev) => ({
                ...prev,
                [pendingCancelItem.signupId]: SignupStatus.CANCEL,
              }))
            );
            queryClient.invalidateQueries({
              queryKey: ['RedVelvetApi', 'getShiftsByEvent', { eventId }],
            });
          },
          onError: () => {
            revertLocalChangeForSignupStatus(pendingCancelItem.signupId);
          },
        }
      );
    }
    setCancelModalVisible(false);
    setPendingCancelItem(null);
  };

  const handleCancelDismiss = () => {
    setCancelModalVisible(false);
    setPendingCancelItem(null);
  };

  const submitEdit = (item: AmazonSignup, newValue: SignupStatus) => {
    const oldStatus = item.signupStatus;
    if (newValue === oldStatus) return;

    if (oldStatus === SignupStatus.SIGNUP && newValue === SignupStatus.CANCEL) {
      setPendingCancelItem(item);
      setCancelModalVisible(true);
    } else if (oldStatus === SignupStatus.CANCEL && newValue === SignupStatus.SIGNUP) {
      setLocalInlineSignupStatusChanges((prev) => ({
        ...prev,
        [item.signupId]: SignupStatus.PENDING,
      }));
      bulkCreateSignup(
        {
          shiftSignupUsersMap: {
            [item.shiftId]: [{ identifierType: 'ALIAS', identifierValue: item.alias }],
          },
        },
        {
          onSuccess: (response) => {
            if (isUserFailedToBeSignedUp(response, item.shiftId, item.alias)) {
              revertLocalChangeForSignupStatus(item.signupId);
            } else {
              const newSignupId =
                response.shiftSignupResponseMap[item.shiftId]?.succeededSignupUsers[0]?.signupId;
              if (newSignupId && newSignupId !== item.signupId) {
                setLocalSignupIdUpdates((prev) => {
                  const updated = { ...prev };
                  // Find the original signupId that maps to item.signupId and point it to newSignupId
                  const originalSignupIdInTheTable = Object.keys(updated).find(
                    (key) => updated[key] === item.signupId
                  );
                  if (originalSignupIdInTheTable) {
                    updated[originalSignupIdInTheTable] = newSignupId;
                  } else {
                    // item.signupId is the original table signupId
                    updated[item.signupId] = newSignupId;
                  }
                  return updated;
                });
                // Remove old key and set status on new key
                setLocalInlineSignupStatusChanges((prev) => {
                  const updated = { ...prev };
                  delete updated[item.signupId];
                  return { ...updated, [newSignupId]: newValue };
                });
              } else {
                setLocalInlineSignupStatusChanges((prev) => ({
                  ...prev,
                  [item.signupId]: newValue,
                }));
              }
            }
            queryClient.invalidateQueries({
              queryKey: ['RedVelvetApi', 'getShiftsByEvent', { eventId }],
            });
          },
          onError: () => {
            revertLocalChangeForSignupStatus(item.signupId);
          },
        }
      );
    }
  };

  return {
    applyLocalChangesForSignupStatus,
    submitEdit,
    cancelModalVisible,
    handleCancelConfirm,
    handleCancelDismiss,
    isEditPending: isCreatePending || isDeletePending,
  };
}
